import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { CodeEditor } from '../CodeEditor/CodeEditor';

interface AsyncOutputPreviewProps {
  evaluateOutput: () => Promise<Record<string, unknown>>;
}

export const AsyncOutputPreview: React.FC<AsyncOutputPreviewProps> = React.memo(({ 
  evaluateOutput 
}) => {
  // Use TanStack Query directly instead of additional state
  const { 
    data: output = {}, 
    error: queryError,
    isLoading,
    isError,
    refetch,
    isFetching
  } = useQuery({
    queryKey: ['outputPreview'],
    queryFn: evaluateOutput,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 0, // Always refetch when requested
  });
  
  // Format error message
  const errorMessage = useMemo(() => {
    if (!isError || !queryError) return null;
    return queryError instanceof Error 
      ? queryError.message 
      : 'Unknown error occurred';
  }, [isError, queryError]);
  
  if (isLoading) {
    return (
      <div className="output-preview-loading">
        <RefreshCw size={28} className="animate-spin" style={{ marginBottom: '0.625rem' }} />
        <div>Generating final output...</div>
      </div>
    );
  }
  
  if (isError && errorMessage) {
    return (
      <div className="output-preview-error">
        <div>Error generating output: {errorMessage}</div>
        <button 
          className="button-subtle" 
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw 
            size={16} 
            className={isFetching ? 'animate-spin' : ''} 
          /> 
          Try Again
        </button>
      </div>
    );
  }
  
  return (
    <div className="async-output-preview">
      <div className="output-preview-actions">
        <button 
          className="button-subtle" 
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw 
            size={16} 
            className={isFetching ? 'animate-spin' : ''} 
          /> 
          Refresh Output
        </button>
      </div>
      <CodeEditor
        value={JSON.stringify(output, null, 2)}
        height="200px"
        readOnly={true}
      />
    </div>
  );
}); 